import { createAsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";
import authService from "../../api/auth/authService";
import storage from "../../utils/storage/cryptoStorage";
import axios from 'axios';
import { t } from "i18next";

interface UserAssPriviliges {
  OFFICE_NAME: string | null;
  OFF_ID: number | null;
  ID: number | null;
}

interface User {
  name: string | null;
  surname: string | null;
  username: string | null;
  email: string | null;
  id: number | null;
  UserAssPriviliges: UserAssPriviliges[] | null;
}

interface AuthenticationState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  validationErrors: Record<string, string[]> | null;
  isAuthenticated: boolean;
  selectedOfficeId: number | null;
}

// localStorage'dan token'� kontrol et ve initial state'i belirle
const storedToken = storage.getLocalStorage("token");
const storedUser = storage.getLocalStorage("user");

const storedSelectedOfficeId = storage.getLocalStorage("lastSelectedOfficeId");

const initialState: AuthenticationState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  token: storedToken,
  loading: false,
  error: null,
  validationErrors: null,
  isAuthenticated: !!storedToken, // Token varsa authenticated kabul et
  selectedOfficeId: storedSelectedOfficeId ? Number(storedSelectedOfficeId) : null,
};

export const authenticationLogin = createAsyncThunk(
  "auth/login",
  async (
    credentials: {
      username: string;
      password: string;
      officeid: number;
      turnstileToken: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await authService.login(
        credentials.username,
        credentials.password,
        credentials.officeid,
        credentials.turnstileToken
      );

      if (response?.data) {
        return response.data;
      }

      return rejectWithValue({ title: 'Unexpected response format' });
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data;
        const errorMessage = errorData?.error || errorData?.message || 'Login failed';
        return rejectWithValue({
          title: errorMessage,
          errors: errorData?.errors || [],
          statuscode: error.response?.status || 500,
        });
      }

      return rejectWithValue({
        title: error?.message || t("errors.unknown-error"),
      });
    }
  }
);

export const authenticationSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state: AuthenticationState) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;

      // localStorage'ı temizle
      storage.removeLocalStorage("token");
      storage.removeLocalStorage("refreshToken");
      storage.removeLocalStorage("user");
      storage.removeLocalStorage("lastSelectedOfficeId");
      try {
        localStorage.removeItem("userId");
      } catch {
      }

      // Redux persist'teki tab verilerini de temizle
      try {
        localStorage.removeItem('persist:bizmed-root');
      } catch (e) {
        console.warn('Tab verilerini temizlerken hata:', e);
      }

      state.selectedOfficeId = null;
    },
    setSelectedOfficeId: (state: AuthenticationState, action: PayloadAction<number>) => {
      state.selectedOfficeId = action.payload;
      storage.saveLocalStorage("lastSelectedOfficeId", action.payload.toString());
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authenticationLogin.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.validationErrors = null;
      state.isAuthenticated = false;
    });
    builder.addCase(
      authenticationLogin.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;

        const user = {
          username: action.payload.username ?? null,
          email: null, // Adjust according to new response data, or get from another source
          id: action.payload.id ?? null,
          name: action.payload.name ?? null,
          surname: action.payload.surname ?? null,
          UserAssPriviliges:
            action.payload.useroffices?.map(
              (office: { id: number; name: string }) => ({
                OFFICE_NAME: office.name ?? null,
                OFF_ID: office.id ?? null,
                ID: office.id ?? null,
              })
            ) ?? [],
        };

        state.user = user;
        state.token = action.payload.token;
        state.isAuthenticated = true;

        // User bilgilerini localStorage'a kaydet (refresh sonrası için)
        storage.saveLocalStorage("user", JSON.stringify(user));
      }
    );
    builder.addCase(authenticationLogin.rejected, (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;

      const payload = action.payload as any;

      state.error = payload.title || payload?.message || t("login.login-failed");
      state.validationErrors = payload?.errors || {};
    });
  },
});

export const { logout, setSelectedOfficeId } = authenticationSlice.actions;
export default authenticationSlice.reducer;
